/* NFC Solutions — Realtime dashboard client
 * ---------------------------------------------------------------
 * A single reconnecting WebSocket per browser tab that subscribes
 * to a business's event stream. Any dashboard component can call
 * `useRealtimeEvent(eventType, handler)` to react to server-side
 * mutations WITHOUT polling.
 *
 * Auth: the socket carries a session token as a `?token=` query
 * param (WS clients can't set Authorization headers).
 *
 * Fallback: if the socket is closed for more than 30 s, callers
 * should rely on their existing polling / refresh mechanisms — the
 * hook exposes an `isConnected` signal for that.
 */

import { useEffect, useRef } from "react";

const RECONNECT_BASE_MS = 1500;
const RECONNECT_MAX_MS = 30000;
// Session 30 — Zombie-socket detection. The previous 25s blind ping
// never verified that a "pong" (or ANY message) actually came back, so
// a half-open TCP connection (WiFi drop, NAT timeout, sleeping tablet)
// stayed "connected" forever while silently receiving nothing. Staff
// dashboards then missed every Runner delivery event and fell back to
// slow HTTP reconciliation — the reported 1-3 minute Runner→Staff lag.
//
// Now: ping every 15s, track the arrival time of EVERY inbound message
// (pong, hello, server keepalive, business events), and run a 10s
// health sweep. If nothing has arrived for 45s (3 missed pongs), the
// socket is provably dead — force-close it, which drives an immediate
// reconnect. The server's fresh "hello" then triggers dashboard
// reconciliation so any missed events are caught up in one fetch.
const PING_INTERVAL_MS = 15000;
const HEALTH_SWEEP_MS = 10000;
const STALE_CONNECTION_MS = 45000;

// One connection is shared across every component in this tab. Each
// `subscribe(handler)` call gets a unique id we can revoke on unmount.
class RealtimeClient {
  constructor() {
    this.socket = null;
    this.subscribers = new Map(); // id → handler
    this._nextId = 1;
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this._pingTimer = null;
    this._connected = false;
    this._targetToken = null;
    this._targetBaseUrl = null;
    this._closedByUser = false;
    this._listeners = new Set(); // "status" listeners
    // Session 30 — liveness tracking for zombie-socket detection.
    this._lastMessageAt = 0;
    this._healthTimer = null;
  }

  connect({ baseUrl, token }) {
    // If already connected with the same identity, do nothing.
    if (this.socket && this._targetToken === token && this._targetBaseUrl === baseUrl) return;
    this.disconnect();
    this._closedByUser = false;
    this._targetToken = token;
    this._targetBaseUrl = baseUrl;
    this._reconnectAttempts = 0;
    this._openSocket();
  }

  disconnect() {
    this._closedByUser = true;
    if (this._reconnectTimer) { clearTimeout(this._reconnectTimer); this._reconnectTimer = null; }
    if (this._pingTimer) { clearInterval(this._pingTimer); this._pingTimer = null; }
    if (this._healthTimer) { clearInterval(this._healthTimer); this._healthTimer = null; }
    if (this.socket) {
      try { this.socket.close(1000, "client-detach"); } catch (e) { console.warn("[realtime] socket close error:", e?.message || e); }
      this.socket = null;
    }
    this._setConnected(false);
  }

  subscribe(handler) {
    const id = this._nextId++;
    this.subscribers.set(id, handler);
    return () => { this.subscribers.delete(id); };
  }

  onStatus(fn) {
    this._listeners.add(fn);
    // Immediately push current state so consumers can render.
    try { fn(this._connected); } catch (e) { console.warn("[realtime] status listener error:", e?.message || e); }
    return () => { this._listeners.delete(fn); };
  }

  _setConnected(v) {
    if (this._connected === v) return;
    this._connected = v;
    this._listeners.forEach((fn) => { try { fn(v); } catch (e) { console.warn("[realtime] status callback error:", e?.message || e); } });
  }

  _openSocket() {
    if (!this._targetToken || !this._targetBaseUrl) return;
    // Build wss:// (or ws:// for localhost) URL from the REST base.
    let wsUrl;
    try {
      const u = new URL(this._targetBaseUrl);
      const scheme = u.protocol === "https:" ? "wss:" : "ws:";
      wsUrl = `${scheme}//${u.host}/api/ws/business?token=${encodeURIComponent(this._targetToken)}`;
    } catch (_) {
      return;
    }
    let sock;
    try { sock = new WebSocket(wsUrl); } catch (_) { this._scheduleReconnect(); return; }
    this.socket = sock;

    sock.onopen = () => {
      this._reconnectAttempts = 0;
      this._setConnected(true);
      // Session 30 — Seed the liveness clock. The server sends a
      // "hello" immediately after accept, so a healthy socket updates
      // this within milliseconds anyway.
      this._lastMessageAt = Date.now();
      // Server-side already sends heartbeats, but many mobile carriers
      // idle-kill sockets < 30 s so we also ping periodically to keep
      // the TCP path warm.
      if (this._pingTimer) clearInterval(this._pingTimer);
      this._pingTimer = setInterval(() => {
        try { sock.send("ping"); } catch (_) { /* noop */ }
      }, PING_INTERVAL_MS);
      // Session 30 — Health sweep: a half-open socket buffers outgoing
      // pings without error and never delivers anything back. If no
      // message (pong / keepalive / event) has arrived within
      // STALE_CONNECTION_MS, declare the socket dead and force-close.
      // onclose then drives an immediate-ish reconnect, and the fresh
      // server "hello" tells dashboards to reconcile.
      if (this._healthTimer) clearInterval(this._healthTimer);
      this._healthTimer = setInterval(() => {
        if (this.socket !== sock) { clearInterval(this._healthTimer); this._healthTimer = null; return; }
        const silentFor = Date.now() - this._lastMessageAt;
        if (silentFor > STALE_CONNECTION_MS) {
          console.warn(`[realtime] connection stale (${Math.round(silentFor / 1000)}s without any message) — forcing reconnect`);
          // Reset backoff so the replacement connection is attempted fast.
          this._reconnectAttempts = 0;
          try { sock.close(4000, "stale-connection"); } catch (_) { /* noop */ }
          // Some browsers delay onclose for dead sockets — proactively
          // detach and reconnect ourselves.
          if (this.socket === sock) {
            this.socket = null;
            this._setConnected(false);
            if (this._pingTimer) { clearInterval(this._pingTimer); this._pingTimer = null; }
            if (this._healthTimer) { clearInterval(this._healthTimer); this._healthTimer = null; }
            if (!this._closedByUser) this._scheduleReconnect();
          }
        }
      }, HEALTH_SWEEP_MS);
    };

    sock.onmessage = (event) => {
      let msg;
      // Session 30 — ANY inbound byte proves the connection is alive.
      this._lastMessageAt = Date.now();
      // Pong replies are plain text — ignore.
      if (event.data === "pong") return;
      try { msg = JSON.parse(event.data); } catch (_) { return; }
      if (!msg || typeof msg.type !== "string") return;
      // Fan out to every subscriber; handlers themselves filter by
      // event type. This keeps subscribers cheap and lets us add new
      // event types without touching this file.
      this.subscribers.forEach((handler) => {
        try { handler(msg); } catch (e) { console.warn("[realtime] subscriber error:", e?.message || e); }
      });
    };

    sock.onerror = () => {
      // The onclose that follows will drive reconnection.
    };

    sock.onclose = () => {
      if (this._pingTimer) { clearInterval(this._pingTimer); this._pingTimer = null; }
      if (this._healthTimer) { clearInterval(this._healthTimer); this._healthTimer = null; }
      this.socket = null;
      this._setConnected(false);
      if (!this._closedByUser) this._scheduleReconnect();
    };
  }

  _scheduleReconnect() {
    if (this._reconnectTimer) return;
    this._reconnectAttempts += 1;
    // Exponential backoff with jitter, capped.
    const backoff = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * Math.pow(1.6, this._reconnectAttempts - 1));
    const jitter = Math.floor(Math.random() * 400);
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._openSocket();
    }, backoff + jitter);
  }
}

// One singleton per tab. Shared across every dashboard component.
export const realtime = new RealtimeClient();

// Session 30.26 — Diagnostics handle (DevTools + the router-only LAN
// simulation use it to inspect / force-close the cloud socket).
if (typeof window !== "undefined") {
  try { window.__nfcsRealtime = realtime; } catch (_) { /* noop */ }
}

/**
 * Session 30.26 — Inject a LAN-mesh-originated event into the SAME
 * subscriber fan-out the cloud socket uses. This is what lets the
 * Kitchen / Bar / Runner / Staff dashboards react to a peer's write in
 * real time when the internet is down but the router is up: the
 * meshBridge translates the mesh envelope into the cloud event shape
 * (`{ type, payload, seq: 0, _via: "mesh" }`) and calls this.
 *
 * `seq: 0` deliberately bypasses the per-dashboard gap/dedupe counters
 * (they only engage for seq > 0), so a later cloud echo of the same
 * mutation is still processed normally and wins as the authoritative
 * copy. Handlers are idempotent by entity id / `_change_id`.
 */
export function injectLocalEvent(msg) {
  if (!msg || typeof msg !== "object" || typeof msg.type !== "string") return false;
  const shaped = { seq: 0, ...msg, _via: msg._via || "mesh" };
  realtime.subscribers.forEach((handler) => {
    try { handler(shaped); } catch (e) { console.warn("[realtime] injected event handler error:", e?.message || e); }
  });
  return true;
}

/** True when the cloud realtime socket is currently open. */
export function isRealtimeConnected() {
  return !!realtime._connected;
}

// Session 27.114 — When the tab wakes from background, mobile browsers
// often silently close idle WebSockets. Force an immediate reconnect
// attempt so runners/staff see fresh events the moment they look at
// their phone, instead of waiting for the next exponential-backoff
// tick (which can be 10+ seconds).
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    try {
      if (document.visibilityState !== "visible") return;
      if (!realtime._targetToken || !realtime._targetBaseUrl) return;
      if (realtime._connected && realtime.socket) return;
      // Cancel any pending backoff timer and reconnect right now.
      if (realtime._reconnectTimer) {
        clearTimeout(realtime._reconnectTimer);
        realtime._reconnectTimer = null;
      }
      realtime._reconnectAttempts = 0;
      realtime._openSocket();
    } catch (_) { /* best-effort */ }
  });
}

// Session 30 — When the browser regains network connectivity, reconnect
// immediately instead of waiting out an exponential-backoff tick. Pairs
// with the health sweep: dead socket detected → close → backoff, then
// the "online" signal short-circuits the wait the moment the network
// path is actually back.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    try {
      if (!realtime._targetToken || !realtime._targetBaseUrl) return;
      if (realtime._connected && realtime.socket) return;
      if (realtime._reconnectTimer) {
        clearTimeout(realtime._reconnectTimer);
        realtime._reconnectTimer = null;
      }
      realtime._reconnectAttempts = 0;
      realtime._openSocket();
    } catch (_) { /* best-effort */ }
  });
}

/**
 * React hook. Subscribe a component to realtime events.
 *
 * @param {string|string[]|null} eventTypes  Which event types the handler
 *   cares about. `null` or `"*"` subscribes to everything.
 * @param {(msg) => void} handler  Called with the raw event message
 *   `{ type, payload, ts }`.
 */
export function useRealtimeEvent(eventTypes, handler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const eventTypesKey = JSON.stringify(eventTypes);
  useEffect(() => {
    const wantsAll = eventTypes == null || eventTypes === "*";
    const wanted = wantsAll ? null : new Set(Array.isArray(eventTypes) ? eventTypes : [eventTypes]);
    const unsub = realtime.subscribe((msg) => {
      if (!wanted || wanted.has(msg.type)) {
        try { handlerRef.current(msg); } catch (e) { console.warn("[realtime] event handler error:", e?.message || e); }
      }
    });
    return unsub;
    // Intentionally NOT depending on `handler` — we use a ref so
    // caller re-renders don't churn subscriptions. `eventTypesKey` is the
    // stable string form of the array (callers pass fresh literals).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventTypesKey]);
}

/** Attach the realtime client to the current session token / base URL. */
export function useRealtimeConnection({ baseUrl, token }) {
  useEffect(() => {
    if (!token || !baseUrl) {
      realtime.disconnect();
      return () => {};
    }
    realtime.connect({ baseUrl, token });
    // Do NOT disconnect on unmount — other dashboards on the same
    // page may still be using the socket. Full disconnect happens on
    // logout via a call to `realtime.disconnect()`.
    return () => {};
  }, [baseUrl, token]);
}
